import { getDocumentBundleDir, getDocumentPathFromAssetsPath } from "./publish-utils";
import type { TraceRun } from "../store/trace-store";
import { tabSessionRegistry } from "../store/tab-session-registry";

const EMPTY_TRACE_NODE = "__empty__";

function normalizeTracePath(path: string | undefined): string | null {
    if (!path) return null;
    return path.replace(/\\/g, "/").replace(/\/+$/, "");
}

export function getDocumentTraceDirPath(documentPath: string): string {
    return `${getDocumentBundleDir(documentPath)}/traces`;
}

export function isDocumentTraceDirPath(path: string): boolean {
    return /\.magic-md\/traces$/.test(path);
}

export function isDocumentTraceItemPath(path: string): boolean {
    return /\.magic-md\/traces\/[^/]+\.mmtrace$/.test(path);
}

export function isDocumentTraceEmptyPath(path: string): boolean {
    return new RegExp(`\\.magic-md/traces/${EMPTY_TRACE_NODE}\\.mmtrace-empty$`).test(path);
}

export function getTraceIdFromPath(path: string): string | null {
    const match = /\.magic-md\/traces\/([^/]+)\.mmtrace$/.exec(path);
    return match ? decodeURIComponent(match[1]) : null;
}

export function getDocumentPathFromTracePath(path: string): string | null {
    if (!/(?:^|\/)[^/]+\.magic-md\/traces(?:\/|$)/.test(path)) return null;
    return getDocumentPathFromAssetsPath(path.replace(/\/traces(?:\/.*)?$/, "/assets"));
}

export function getTraceRunDocumentPath(run: TraceRun): string | null {
    const tabId = tabSessionRegistry.getTabForSession(run.sessionId);
    const registryFilePath = tabId ? tabSessionRegistry.getFilePathForTab(tabId) : undefined;
    return normalizeTracePath(run.filePath ?? registryFilePath);
}

export function traceRunBelongsToDocument(run: TraceRun, documentPath: string | null): boolean {
    if (!documentPath) return true;
    const runPath = getTraceRunDocumentPath(run);
    const targetPath = normalizeTracePath(documentPath);
    return !!runPath && !!targetPath && runPath === targetPath;
}

export function traceRunVisibleInTracePanel(run: TraceRun, documentPath: string | null): boolean {
    if (!documentPath) return true;
    const runPath = getTraceRunDocumentPath(run);
    if (!runPath) return true;
    const targetPath = normalizeTracePath(documentPath);
    return !!targetPath && runPath === targetPath;
}

export function traceRunBelongsToTab(
    run: TraceRun,
    context: { documentPath?: string | null; sessionId?: string | null },
): boolean {
    const documentPath = context.documentPath?.trim() || null;
    const sessionId = context.sessionId?.trim() || null;
    return (
        (documentPath ? traceRunBelongsToDocument(run, documentPath) : false) ||
        (!!sessionId && run.sessionId === sessionId)
    );
}

export function hasRunningTraceForTab(
    runs: TraceRun[],
    context: { documentPath?: string | null; sessionId?: string | null },
): boolean {
    return runs.some((run) =>
        run.status === "running" && traceRunBelongsToTab(run, context),
    );
}

export function buildTraceVirtualChildren(
    traceDirPath: string,
    runs: TraceRun[],
    persistedTraceIds: string[] = [],
) {
    const seen = new Set<string>();
    const items: Array<{ traceId: string; persisted: boolean }> = [];
    for (const run of runs) {
        seen.add(run.traceId);
        items.push({ traceId: run.traceId, persisted: false });
    }
    for (const traceId of [...persistedTraceIds].sort((a, b) => b.localeCompare(a))) {
        if (seen.has(traceId)) continue;
        items.push({ traceId, persisted: true });
    }
    if (items.length === 0) {
        return [
            {
                name: "No trace runs",
                path: `${traceDirPath}/${EMPTY_TRACE_NODE}.mmtrace-empty`,
                isDirectory: false,
                isVirtual: true as const,
                loaded: true,
                expanded: false,
            },
        ];
    }
    return items.map((item) => ({
        name: `${encodeURIComponent(item.traceId)}.mmtrace`,
        path: `${traceDirPath}/${encodeURIComponent(item.traceId)}.mmtrace`,
        isDirectory: false,
        isVirtual: true as const,
        loaded: item.persisted,
        expanded: false,
    }));
}
